import React from 'react';
import { FormGroup } from '@blueprintjs/core';
import {
  FieldArray,
  Markup,
  MarkupProps,
  useField,
  ArrayHelpers,
  TextInput
} from '@lightbeamhealth/blueprint-form';
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';

import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import { REGEX_EMAIL } from '../../../libs/regex';

import SelectContact from './select';
import { ContactCCData } from './types';

export interface ContactCCProps extends MarkupProps {
  excludeIds?: number[];
  onRemove?: (event: React.MouseEvent) => any;
}

export const ContactCC: React.FC<ContactCCProps> = ({
  name,
  onRemove,
  excludeIds
}): JSX.Element => {
  const id = Markup.getID();
  const [field, meta, helpers] = useField<ContactCCData>({
    name,
    validate: (value: ContactCCData): string => {
      let error: string = null;
      if (value.isEmail) {
        if (!value.email) {
          error = 'The email address cannot be blank!';
        } else if (!REGEX_EMAIL.test(value.email)) {
          error = 'The email address is not in valid format!';
        }
      } else {
        if (!value.copyId) {
          error = 'A contact must be selected!';
        }
      }
      return error;
    }
  });
  const { value } = field;
  const label = value.isEmail ? 'Email to CC' : 'Contact to CC';
  const toggleMode = (): void => {
    const newValue: ContactCCData = {
      ...value,
      isEmail: !value.isEmail
    };
    if (newValue.isEmail) {
      newValue.copyId = null;
    } else {
      newValue.email = '';
    }
    helpers.setValue(newValue);
  };
  return (
    <React.Fragment>
      <FormGroup
        label={
          <label htmlFor={id}>
            {label}
            <Button
              className="ml-4"
              intent="primary"
              bare
              variant="minimal"
              onClick={toggleMode}
            >
              {value.isEmail ? 'Use Contact Instead' : 'Use Email Instead'}
            </Button>
          </label>
        }
        labelFor={id}
        intent={meta.touched && meta.error ? 'danger' : 'none'}
        helperText={meta.touched && meta.error ? meta.error : null}
      >
        <Row>
          <Col xs={20}>
            {value.isEmail ? (
              <TextInput
                label="Email to CC"
                name={`${name}.email`}
                placeholder="abc@example.com"
                bare
                fill
              />
            ) : (
              <SelectContact
                id={id}
                label="Contact to CC"
                name={`${name}.copyId`}
                excludeIds={excludeIds}
                bare
                fill
              />
            )}
          </Col>
          <Col xs={4}>
            <Button
              intent="danger"
              icon={<Icon className="text-white" icon={faTrash} />}
              aria-label="Remove CC"
              onClick={onRemove}
            />
          </Col>
        </Row>
      </FormGroup>
    </React.Fragment>
  );
};

export interface ContactCCGroupProps extends MarkupProps {
  maxItems?: number;
  excludeIds?: number[];
}

const ContactCCGroup: React.FC<ContactCCGroupProps> = ({
  name,
  large,
  maxItems,
  excludeIds
}): JSX.Element => {
  const [field] = useField<ContactCCData[]>(name);
  const { value: entries } = field;
  const remove = (helpers: ArrayHelpers, i: number): void => {
    helpers.remove<ContactCCData>(i);
  };
  const add = (helpers: ArrayHelpers): void => {
    if (maxItems > 0 && entries.length >= maxItems) {
      return;
    }
    const entry: ContactCCData = {
      id: null,
      isEmail: false,
      email: '',
      copyId: null
    };
    helpers.push(entry);
  };
  return (
    <FieldArray
      name={name}
      render={(helpers) => (
        <>
          {entries.map((_, i) => (
            <ContactCC
              key={i}
              name={`${name}.${i}`}
              excludeIds={excludeIds}
              onRemove={() => remove(helpers, i)}
              large={large}
            />
          ))}
          <div>
            <Button
              intent="primary"
              icon={<Icon className="text-white" icon={faPlus} />}
              onClick={() => add(helpers)}
            >
              Add Another CC Contact
            </Button>
          </div>
        </>
      )}
    />
  );
};

export default ContactCCGroup;
